'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';

import { COUNTRIES, Country, DEFAULT_COUNTRY } from '@/data/countries';
import { trackEvent } from '@/lib/analytics';
import { detectDeviceOSClient, getOrCreateAppUdid } from '@/lib/device';
import { getWebPushToken } from '@/lib/firebase-messaging';

declare global {
  interface Window {
    hcaptcha?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => number;
      reset: (id?: number) => void;
    };
  }
}

type AuthMode = 'login' | 'signup';
type IdentifierType = 'email' | 'phone';

interface AuthOtpFormProps {
  mode: AuthMode;
  returnTo?: string;
}

type StartResponse = {
  session: string;
  challengeParameters?: Record<string, string>;
};

export function AuthOtpForm({ mode, returnTo }: AuthOtpFormProps) {
  const router = useRouter();
  const [identifierType, setIdentifierType] = useState<IdentifierType>('email');
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [nickname, setNickname] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [step, setStep] = useState<'collect' | 'verify'>('collect');
  const [code, setCode] = useState('');
  const [session, setSession] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaContainerRef = useRef<HTMLDivElement | null>(null);
  const captchaWidgetIdRef = useRef<number | string | null>(null);
  const captchaScriptLoadedRef = useRef(false);
  const hcaptchaSiteKey = process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY;
  const [applicationUdid, setApplicationUdid] = useState<string | null>(null);
  const [pushToken, setPushToken] = useState<string | null>(null);
  const deviceOS = useMemo(() => detectDeviceOSClient(), []);
  const captchaRequired = Boolean(hcaptchaSiteKey);
  const captchaMet = !captchaRequired || Boolean(captchaToken);

  useEffect(() => {
    setIdentifier('');
    setError(null);
    setInfo(null);
  }, [identifierType]);

  useEffect(() => {
    if (!hcaptchaSiteKey) return;

    const renderWidget = () => {
      if (!window.hcaptcha || !captchaContainerRef.current || captchaWidgetIdRef.current !== null) return;
      captchaWidgetIdRef.current = window.hcaptcha.render(captchaContainerRef.current, {
        sitekey: hcaptchaSiteKey,
        callback: (token: string) => setCaptchaToken(token),
        'expired-callback': () => setCaptchaToken(null),
        'error-callback': () => setCaptchaToken(null),
      });
    };

    if (captchaScriptLoadedRef.current) {
      renderWidget();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      captchaScriptLoadedRef.current = true;
      renderWidget();
    };
    document.body.appendChild(script);
    return () => {
      if (script.parentNode && !captchaScriptLoadedRef.current) {
        script.parentNode.removeChild(script);
      }
    };
  }, [hcaptchaSiteKey, identifierType]);

  const formattedIdentifier = useMemo(() => {
    if (identifierType === 'email') return identifier.trim();
    const trimmed = identifier.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('+')) return trimmed;
    const digitsOnly = trimmed.replace(/[^0-9]/g, '');
    return `${country.dialCode}${digitsOnly}`;
  }, [identifier, identifierType, country]);

  useEffect(() => {
    const udid = getOrCreateAppUdid();
    setApplicationUdid(udid);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Avoid prompting for notifications during auth; only fetch if already granted.
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
      const token = await getWebPushToken();
      if (!cancelled) {
        setPushToken(token);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleStart = useCallback(async () => {
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      if (identifierType === 'phone' && country.dialCode !== '+1') {
        setError('Phone sign-in is US/Canada only. Please use email to continue.');
        setLoading(false);
        return;
      }

      const payload: Record<string, unknown> = {
        mode,
        identifier: formattedIdentifier,
      };
      if (mode === 'signup') {
        payload.nickname = nickname.trim();
      } else {
        if (applicationUdid) {
          payload.applicationUDID = applicationUdid;
          payload.deviceOS = deviceOS;
        }
        if (pushToken) {
          payload.devicePushID = pushToken;
        }
      }
      if (hcaptchaSiteKey && captchaToken) {
        payload.captchaToken = captchaToken;
      }

      const response = await fetch('/auth/otp/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const { error: message } = await response.json();
        throw new Error(message || 'Unable to send code.');
      }

      const data: StartResponse = await response.json();
      if (!data.session) {
        throw new Error('Unable to start verification.');
      }

      setSession(data.session);
      setUsername(formattedIdentifier);
      if (window.hcaptcha && captchaWidgetIdRef.current !== null) {
        try {
          // Reset without passing the widget id to avoid sporadic "invalid-captcha-id" errors.
          window.hcaptcha.reset();
        } catch (err) {
          console.warn('hCaptcha reset failed', err);
          captchaWidgetIdRef.current = null;
        }
        setCaptchaToken(null);
      }
      setStep('verify');
      setCode('');
      setInfo('Verification code sent. Enter the 6 digit code to continue.');
      restartTimer();
      trackEvent('auth_code_sent', {
        mode,
        identifierType,
        identifier: anonymizeIdentifierValue(formattedIdentifier, identifierType),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to send code.';
      setError(message);
      trackEvent('auth_code_failure', { mode, identifierType, reason: message });
    } finally {
      setLoading(false);
    }
  }, [formattedIdentifier, mode, nickname, identifierType, country.dialCode, captchaToken, hcaptchaSiteKey, applicationUdid, deviceOS, pushToken]);

  const restartTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setResendTimer(30);
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = useCallback(async () => {
    if (!username || resendTimer > 0) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/auth/otp/resend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      });

      if (!response.ok) {
        const { error: message } = await response.json();
        throw new Error(message || 'Unable to resend code.');
      }

      const data: StartResponse = await response.json();
      if (data.session) {
        setSession(data.session);
      }
      setInfo('Code resent. Check your email or phone.');
      restartTimer();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to resend code.';
      setError(message);
      trackEvent('auth_resend_failure', { mode, identifierType, reason: message });
    } finally {
      setLoading(false);
    }
  }, [username, resendTimer, mode, identifierType]);

  const destination = useMemo(() => {
    if (returnTo && returnTo.startsWith('/')) {
      return returnTo;
    }
    return '/main-app';
  }, [returnTo]);

  const handleConfirm = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    setInfo(null);

    try {
      const response = await fetch('/auth/otp/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          mode === 'login'
            ? {
                username,
                codeConfirmation: code.trim(),
                session,
                returnTo: destination,
                applicationUDID: applicationUdid ?? undefined,
                deviceOS,
                devicePushID: pushToken ?? undefined,
              }
            : {
                username,
                codeConfirmation: code.trim(),
                session,
                returnTo: destination,
              },
        ),
      });

      if (!response.ok) {
        const { error: message } = await response.json();
        throw new Error(message || 'Unable to verify code.');
      }

      const data = await response.json();
      trackEvent('auth_success', {
        mode,
        identifierType,
        identifier: anonymizeIdentifierValue(username, identifierType),
      });
      router.replace(data.redirectTo ?? destination);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to verify code.';
      setError(message);
      trackEvent('auth_failure', { mode, identifierType, reason: message });
    } finally {
      setLoading(false);
    }
  }, [session, username, code, router, mode, identifierType, destination, applicationUdid, deviceOS, pushToken ]);

  const onSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (step === 'collect') {
        await handleStart();
      } else {
        await handleConfirm();
      }
    },
    [step, handleStart, handleConfirm],
  );

  const canSubmitCollect = useMemo(() => {
    if (identifierType === 'email') {
      return Boolean(identifier.trim());
    }
    return Boolean(identifier.replace(/\D+/g, ''));
  }, [identifier, identifierType]);

  useEffect(() => () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  }, []);

  const canSubmitVerify = useMemo(() => code.trim().length === 6, [code]);

  return (
    <div className="mx-auto w-full max-w-md space-y-6">
      <header className="space-y-2 text-center">
        <h1 className="text-3xl font-semibold text-foreground">{mode === 'login' ? 'Log in to WhoPlays' : 'Sign up for WhoPlays'}</h1>
        <p className="text-sm text-muted-foreground">
          {mode === 'login'
            ? 'Use your email or mobile number to receive a one-time passcode.'
            : 'Create your account with an email or mobile number. We will send you a one-time passcode to confirm.'}
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-5">
        {step === 'collect' ? (
          <>
            {mode === 'signup' ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground" htmlFor="nickname">
                  Display name
                </label>
                <input
                  id="nickname"
                  name="nickname"
                  value={nickname}
                  onChange={(event) => setNickname(event.target.value)}
                  required
                  className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            ) : null}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIdentifierType('email')}
                className={`flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition ${
                  identifierType === 'email'
                    ? 'border-primary bg-primary text-primary-foreground shadow'
                    : 'border-border bg-background text-foreground hover:bg-muted'
                }`}
              >
                Email
              </button>
              <button
                type="button"
                onClick={() => setIdentifierType('phone')}
                className={`flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition ${
                  identifierType === 'phone'
                    ? 'border-primary bg-primary text-primary-foreground shadow'
                    : 'border-border bg-background text-foreground hover:bg-muted'
                }`}
              >
                Phone
              </button>
            </div>

            {identifierType === 'email' ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground" htmlFor="email">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground" htmlFor="phone">
                  Mobile number
                </label>
                <div className="flex gap-2">
                  <label htmlFor="country" className="sr-only">
                    Country code
                  </label>
                  <select
                    id="country"
                    name="country"
                    className="w-32 rounded-lg border border-border bg-background px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    value={country.code}
                    onChange={(event) => {
                      const next = COUNTRIES.find((item) => item.code === event.target.value);
                      if (next) setCountry(next);
                    }}
                  >
                    {COUNTRIES.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.flag} {item.dialCode}
                      </option>
                    ))}
                  </select>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={identifier}
                    onChange={(event) => setIdentifier(event.target.value)}
                    className="flex-1 rounded-lg border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground">Include your area code. We will send a 6 digit code via SMS.</p>
              </div>
            )}
            {hcaptchaSiteKey ? (
              <div className="mt-3 text-center">
                <p className="text-xs text-muted-foreground">Help us block bots by completing the challenge.</p>
                <div ref={captchaContainerRef} className="mt-2 inline-flex justify-center" />
              </div>
            ) : null}
          </>
        ) : (
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="code">
              Verification code
            </label>
            <input
              id="code"
              name="code"
              type="tel"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              className="w-full rounded-lg border border-border bg-background px-4 py-3 text-center text-lg tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{username}</span>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendTimer > 0 || loading}
                className="text-primary hover:text-primary/80 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
              </button>
            </div>
          </div>
        )}

        {error ? (
          <p className="rounded-lg border border-red-300 bg-surface/60 px-3 py-2 text-sm text-red-300">{error}</p>
        ) : null}
        {info ? (
          <p className="rounded-lg border border-primary/20 bg-primary/10 px-3 py-2 text-sm text-primary">{info}</p>
        ) : null}

        <button
          type="submit"
          disabled={
            loading ||
            (step === 'collect'
              ? !canSubmitCollect || (hcaptchaSiteKey ? !captchaToken : false)
              : !canSubmitVerify)
          }
          className="w-full rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow transition hover:-translate-y-0.5 hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Please wait…' : step === 'collect' ? 'Send code' : 'Verify and continue'}
        </button>
      </form>

      <div className="space-y-3 text-center">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Or continue with</p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              const params = new URLSearchParams({ returnTo: destination }).toString();
              window.location.href = `/auth/social/google?${params}`;
            }}
            disabled={!captchaMet}
            className="flex items-center justify-center gap-3 rounded-full border border-border px-5 py-3 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span>Continue with Google</span>
          </button>
          <button
            type="button"
            onClick={() => {
              const params = new URLSearchParams({ returnTo: destination }).toString();
              window.location.href = `/auth/social/apple?${params}`;
            }}
            disabled={!captchaMet}
            className="flex items-center justify-center gap-3 rounded-full border border-border px-5 py-3 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span>Continue with Apple</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function anonymizeIdentifierValue(value: string, type: IdentifierType) {
  if (type === 'email') {
    const [local, domain] = value.split('@');
    if (!domain) return 'email';
    return `${local.slice(0, 2)}***@${domain}`;
  }
  const sanitized = value.replace(/\s+/g, '');
  if (sanitized.length <= 3) return sanitized;
  return `${sanitized.slice(0, 3)}***`;
}
